import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { COMPANY } from "@/lib/config";
import { useLang } from "@/i18n/LanguageContext";
import QuoteForm from "@/components/QuoteForm";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import {
  HandCoins, Gavel, MessageSquare, Users, CalendarDays, TrendingUp,
  Sparkles, Puzzle, Workflow, ListChecks, Brain, ClipboardList, Mail,
  CreditCard, Banknote, Repeat, Landmark, ArrowLeft, Phone, Check, X,
  Building2, Church, GraduationCap, HeartHandshake, ArrowRight,
  Smartphone, Nfc, Send,
} from "lucide-react";

const WORSHIP_IMG = "https://images.unsplash.com/photo-1600288480699-0b0d8a456dd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const VIEWPORT_ONCE = { once: true };

const AUDIENCES = [
  { icon: Church, label: "Churches" },
  { icon: HeartHandshake, label: "Ministries" },
  { icon: Building2, label: "Nonprofits" },
  { icon: GraduationCap, label: "Schools" },
];

const NO_FEES = ["No Credit Card Fees", "No Transaction Fees", "No Swipe Fees", "No Deposit Fees"];

const GIFT_TYPES = [
  { icon: CreditCard, label: "Credit / Debit Cards" },
  { icon: Banknote, label: "ACH / eCheck" },
  { icon: Repeat, label: "Recurring Gifts" },
  { icon: Landmark, label: "Donor-Advised Funds" },
];

const FEATURES = [
  { icon: HandCoins, title: "Donations", desc: "Secure online giving, recurring gifts, text-to-give & donor management." },
  { icon: Gavel, title: "Events & Auctions", desc: "Silent & live auctions, fund-a-need, raffles, ticketing & more." },
  { icon: MessageSquare, title: "Messaging", desc: "Email & SMS campaigns, automation, segmentation & engagement tracking." },
  { icon: Users, title: "Volunteer Management", desc: "Scheduling, team coordination, roles & attendance tracking." },
  { icon: CalendarDays, title: "Reservations & Rooms", desc: "Room & resource scheduling and calendar management." },
  { icon: TrendingUp, title: "Fundraising Tools", desc: "FunRun / pledge campaigns and digital fundraising tools." },
  { icon: Sparkles, title: "Reporting & AI Insights", desc: "Real-time dashboards, custom reports, and AI-powered insights." },
  { icon: Puzzle, title: "Integrations", desc: "QuickBooks, Planning Center, Stripe, Mailchimp & more." },
];

const AUTOMATION = [
  { icon: Workflow, title: "Automation Workflows", desc: "Design if/then workflows that run on autopilot — welcome donors, trigger follow-ups, and re-engage lapsed givers." },
  { icon: ListChecks, title: "Tasks & Reminders", desc: "Assign tasks, set due dates, automate reminders and track progress in one place." },
  { icon: Brain, title: "AI Reporting & Insights", desc: "AI-powered trend analysis that predicts giving & donor behavior with instant, easy-to-read reports." },
  { icon: ClipboardList, title: "Custom & Smart Forms", desc: "Donation forms, registrations, and volunteer applications with conditional logic — fully branded." },
  { icon: Mail, title: "Unlimited Emails", desc: "Send unlimited emails to unlimited contacts. No extra fees. No restrictions." },
];

const STATS = [
  { value: "1M+", label: "Donors Managed" },
  { value: "$500M+", label: "Donations Processed" },
  { value: "99.9%", label: "Uptime SLA" },
  { value: "4K+", label: "Organizations Served" },
];

const SUITE = [
  "Online & Mobile Giving", "CRM", "Volunteer Scheduling", "Kiosks", "Text Giving",
  "Unlimited Emails", "Mobile Apps", "Automation", "Silent & Live Auctions", "Raffles",
  "POS", "Robust Back Office", "Reports", "Events", "Sign Ups", "Check-Ins",
  "Tap Plates", "Memberships", "Reservations", "Donor Tracking",
];

const HIGHLIGHTS = [
  { icon: Smartphone, title: "Custom Mobile Apps", desc: "Your own branded app in the app stores — at a fraction of what others charge." },
  { icon: Nfc, title: "Tap Plates", desc: "Increase engagement up to 300% with the must-have tap-to-give plate. No software fee.", badge: "+300% engagement" },
  { icon: Send, title: "Email & Text Program", desc: "Unlimited database, unlimited emails, text giving & automation — and so much more." },
];

export default function GiveHubPage() {
  const { d } = useLang();
  return (
    <div className="min-h-screen bg-white font-sans text-foreground">
      {/* Nav — matches main site */}
      <header className="sticky top-0 z-50 border-b border-white/40 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <div className="flex items-center gap-3">
            <a href="/" data-testid="givehub-logo" className="flex items-center">
              <img src={COMPANY.logo} alt={COMPANY.legalName} className="h-9 w-auto sm:h-10" />
            </a>
            <span className="hidden rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary sm:inline">GiveHub</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="back-to-home" className="hidden items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground sm:flex">
              <ArrowLeft className="h-4 w-4" /> Home
            </Link>
            <LanguageSelector />
            <a href={COMPANY.phoneHref} data-testid="givehub-call">
              <Button className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white">
                <Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent/25 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:px-12 lg:grid-cols-2 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/50 bg-accent/15 px-4 py-1.5 text-sm font-semibold text-accent">
              <HandCoins className="h-4 w-4" /> {d.ghp.tag}
            </span>
            <p className="mt-6 font-display text-2xl font-semibold text-primary">GiveHub</p>
            <h1 className="mt-1 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              One Platform. <span className="text-primary">Infinite Impact.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg text-white/70">
              Everything nonprofits, churches, ministries, and schools need to engage, grow, and thrive — all in one place. Accept donations, offerings, and tithes with zero fees.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href="#givehub-quote" data-testid="hero-givehub-cta">
                <Button className="h-12 w-full rounded-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white sm:w-auto">
                  Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href={COMPANY.phoneHref} data-testid="hero-givehub-call">
                <Button variant="outline" className="h-12 w-full rounded-full border-2 border-white/40 bg-transparent px-8 text-base font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                  <Phone className="mr-2 h-4 w-4" /> {COMPANY.phone}
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm">
              {["Increase Giving", "Save Time", "Engage Donors", "Strengthen Your Mission"].map((b) => (
                <span key={b} className="inline-flex items-center gap-2 text-white/80">
                  <Check className="h-4 w-4 text-accent" /> {b}
                </span>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={1} className="relative">
            <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
              <img src={WORSHIP_IMG} alt="Community worship" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white p-5 shadow-xl">
              <p className="font-display text-3xl font-semibold text-primary">$500M+</p>
              <p className="text-sm text-muted-foreground">in donations processed</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Audiences */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:px-12">
        <p className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">Built for mission-driven organizations</p>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {AUDIENCES.map((a) => (
            <div key={a.label} className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-secondary p-6 text-center transition-all hover:-translate-y-1 hover:shadow-md">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <a.icon className="h-6 w-6" />
              </span>
              <p className="font-display text-lg font-semibold">{a.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-secondary py-12">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 md:grid-cols-4 md:px-12" data-testid="givehub-stats">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-display text-4xl font-semibold text-primary">{s.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Complete Donation Product Suite */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12" data-testid="givehub-suite">
        <div className="mb-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-accent">One platform, everything included</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">The complete donation product suite</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-muted-foreground">State-of-the-art donation forms plus a full toolkit to manage donors, engage members, and grow giving.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {SUITE.map((item) => (
            <span key={item} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:border-primary/40 hover:text-primary">
              <Check className="h-4 w-4 text-accent" /> {item}
            </span>
          ))}
          <span className="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">&amp; much more</span>
        </div>
      </section>


      {/* Pricing / Offer */}
      <section id="givehub-pricing" className="bg-secondary py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <div className="mb-10 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground">
              Limited-Time Offer
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Simple, transparent pricing</h2>
            <p className="mt-3 text-lg text-muted-foreground">We waive <strong>all costs</strong> of opening a merchant account with us.</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-5">
            {/* Price card */}
            <motion.div
              initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}
              data-testid="givehub-price-card"
              className="lg:col-span-2 flex flex-col rounded-3xl bg-primary p-8 text-primary-foreground shadow-2xl"
            >
              <p className="text-sm font-medium uppercase tracking-wide text-primary-foreground/70">GiveHub Platform</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl font-semibold">$50</span>
                <span className="mb-1.5 text-primary-foreground/70">/month</span>
              </div>
              <p className="mt-2 text-primary-foreground/80">Just <strong>$600 / year</strong></p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /> First month FREE — billing starts month 2</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /> Merchant account setup — $0, fully waived</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /> Billed for the full year at the limited-time rate</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /> All platform features included</li>
              </ul>
              <a href="#givehub-quote" data-testid="pricing-cta" className="mt-8">
                <Button className="h-12 w-full rounded-full bg-white text-base font-semibold text-primary hover:bg-white/90">
                  Claim This Offer
                </Button>
              </a>
            </motion.div>

            {/* No fees */}
            <motion.div
              initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={1}
              className="lg:col-span-3 rounded-3xl border border-border bg-white p-8 shadow-sm"
            >
              <h3 className="font-display text-xl font-semibold">Keep 100% of every gift</h3>
              <p className="mt-2 text-muted-foreground">No hidden charges eating into your donations, offerings, and tithes.</p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                {NO_FEES.map((f) => (
                  <div key={f} className="flex items-center gap-3 rounded-2xl bg-secondary p-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                      <X className="h-5 w-5" />
                    </span>
                    <p className="font-medium leading-tight">{f}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl bg-secondary p-5">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">Accept every type of gift</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  {GIFT_TYPES.map((g) => (
                    <span key={g.label} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium">
                      <g.icon className="h-4 w-4 text-accent" /> {g.label}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 lg:py-24">
        <div className="mb-12 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">All in one place</p>
          <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Everything you need. Nothing you don't.</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((ft, i) => (
            <motion.div
              key={ft.title}
              initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={i % 4}
              className="rounded-3xl border border-border bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <ft.icon className="h-6 w-6" />
              </span>
              <h3 className="font-display text-lg font-semibold">{ft.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{ft.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-secondary py-20 lg:py-24" data-testid="givehub-highlights">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">Members love these</p>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Stand-out tools that boost giving</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.title}
                initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={i}
                className="relative overflow-hidden rounded-3xl border border-border bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                {h.badge && (
                  <span className="absolute right-4 top-4 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">{h.badge}</span>
                )}
                <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <h.icon className="h-7 w-7" />
                </span>
                <h3 className="font-display text-xl font-semibold">{h.title}</h3>
                <p className="mt-2 text-muted-foreground">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation */}
      <section className="bg-slate-950 py-20 text-white lg:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid gap-8 lg:grid-cols-2 lg:items-end">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-accent">Automate. Engage. Grow.</p>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Powerful automation that works for you</h2>
              <p className="mt-4 max-w-lg text-white/60">Put your mission on autopilot with intelligent workflows, tasks, and unlimited communication.</p>
            </div>
            <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10">
              <img src={COMMUNITY_IMG} alt="Community together" className="h-52 w-full object-cover" />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AUTOMATION.map((a, i) => (
              <motion.div
                key={a.title}
                initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={i % 3}
                className="rounded-3xl border border-white/10 bg-white/5 p-6"
              >
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-accent/15 text-accent">
                  <a.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-lg font-semibold">{a.title}</h3>
                <p className="mt-2 text-sm text-white/60">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get started / lead form */}
      <section id="givehub-quote" className="scroll-mt-20 bg-slate-950 py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
          <div className="text-white">
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Let's help more nonprofits thrive.
            </h2>
            <p className="mt-4 max-w-md text-lg text-white/70">
              Tell us about your organization and we'll set up your GiveHub platform and waive your merchant account costs. A specialist will reach out within 24 hours.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a href={COMPANY.phoneHref} data-testid="givehub-bottom-call">
                <Button className="h-14 w-full rounded-full bg-primary px-8 text-lg font-bold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white sm:w-auto">
                  <Phone className="mr-2 h-5 w-5" /> {COMPANY.phone}
                </Button>
              </a>
            </div>
          </div>
          <div>
            <QuoteForm source="givehub" />
          </div>
        </div>
      </section>

      {/* Footer — matches main site */}
      <footer className="bg-slate-950 py-12 text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 md:flex-row md:px-12">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white p-2.5 shadow-lg">
              <img src={COMPANY.logo} alt={COMPANY.legalName} className="h-8 w-auto" />
            </div>
            <p className="text-sm text-white/50">GiveHub by {COMPANY.name}</p>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/60">
            <Link to="/" className="hover:text-white" data-testid="footer-back-home">← Back to {COMPANY.shortName}</Link>
            <a href={COMPANY.phoneHref} className="hover:text-white">{COMPANY.phone}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
